import Pusher, { type Channel } from 'pusher-js'
import type { PresenceState } from '../../domain/types'
import type { Realtime } from '../../realtime/Realtime'
import type { RealtimeEvent, RealtimeEventName } from '../../realtime/events'

const PUSHER_EVENTS: RealtimeEventName[] = [
  'TicketChanged',
  'TicketNew',
  'TicketCalled',
  'TicketStart',
  'TicketComplete',
  'TicketCancel',
  'TicketCheckin',
  'TicketCheckinSmartCode',
  'TicketReopened',
  'TicketConfirm',
  'TransferredTicket',
  'PrevisionChanged',
  'PriorityChanged',
  'AttendanceTypeChanged',
]

export class PusherRealtime implements Realtime {
  private client: Pusher | null = null
  private listeners = new Set<(e: RealtimeEvent) => void>()
  private channels = new Map<string, Channel>()
  private serviceConfigIds: number[] = []
  private sessionIds: number[] = []
  private aggregatorIds: string[] = []
  private ticketIds: number[] = []

  constructor(private key: string, private cluster: string) {}

  start(): void {
    if (!this.key || this.client) {return}
    this.client = new Pusher(this.key, { cluster: this.cluster })
    this.syncChannels()
  }

  stop(): void {
    this.channels.forEach((channel, name) => {
      channel.unbind_all()
      this.client?.unsubscribe(name)
    })
    this.channels.clear()
    this.client?.disconnect()
    this.client = null
  }

  on(cb: (e: RealtimeEvent) => void): () => void {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  // Pusher carrega eventos de ticket, não presença. A presença (online/away/offline)
  // vem do FirebaseRealtime; no CompositeRealtime o Pusher delega retornando 'offline'
  // (o primeiro delegate com estado != 'offline' vence). Implementar presença aqui
  // exigiria Pusher Presence Channels + endpoint de auth no websocket — desnecessário
  // enquanto o Firebase for a fonte de presença.
  presenceOf(_ticketIdOrAggId: string | number): PresenceState {
    return 'offline'
  }

  watchServiceConfig(scid: number): void {
    this.watchChannels([scid], this.sessionIds, this.aggregatorIds, this.ticketIds)
  }

  watchChannels(
    serviceConfigIds: number[],
    sessionIds: number[],
    aggregatorIds?: string[],
    ticketIds?: number[]
  ): void {
    this.serviceConfigIds = [...new Set(serviceConfigIds.filter((id) => id > 0))]
    this.sessionIds = [...new Set(sessionIds.filter((id) => id > 0))]
    this.aggregatorIds = [...new Set((aggregatorIds || []).filter((id) => !!id))]
    this.ticketIds = [...new Set((ticketIds || []).filter((id) => id > 0))]
    this.syncChannels()
  }

  private syncChannels(): void {
    if (!this.client) {return}

    const next = new Set([
      ...this.serviceConfigIds.map((id) => `serviceConfig-${id}`),
      ...this.serviceConfigIds.map((id) => `service-config-${id}`),
      ...this.sessionIds.map((id) => `session-${id}`),
      ...this.aggregatorIds.map((id) => `schedule-${id}`),
      ...this.ticketIds.map((id) => `ticket-${id}`),
    ])

    // Diff em vez de full rebuild: só desinscreve dos canais que saíram do
    // escopo e só assina os que entraram. Sem isso, navegar de Detalhes A pra
    // Detalhes B (mesmo aggregatorId, mas tickets diferentes) causava
    // unsubscribe de TODOS os canais + resubscribe de TODOS = ~30-50 ms de janela
    // cega onde eventos realtime são perdidos. Diff preserva os canais
    // estáveis (fila + sessão) e só mexe no que mudou.
    this.channels.forEach((channel, name) => {
      if (next.has(name)) {return}
      channel.unbind_all()
      this.client?.unsubscribe(name)
      this.channels.delete(name)
    })

    next.forEach((name) => {
      if (this.channels.has(name)) {return}
      const channel = this.client!.subscribe(name)
      PUSHER_EVENTS.forEach((eventName) => {
        channel.bind(eventName, (data: unknown) => this.emit({ name: eventName, ticketId: this.readTicketId(data), data }))
      })
      this.channels.set(name, channel)
    })
  }

  private readTicketId(data: unknown): number | undefined {
    if (!data || typeof data !== 'object') {return undefined}
    const value = (data as Record<string, unknown>).ticketId ?? (data as Record<string, unknown>).TicketId
    return typeof value === 'number' ? value : undefined
  }

  private emit(event: RealtimeEvent): void {
    this.listeners.forEach((cb) => cb(event))
  }
}
